import {
  applyDraw,
  applyMove,
  applyPass,
  canCurrentPlayerAct,
  claimMuggins,
  type GameStateData,
} from '../../game/GameState.js';
import { hasLegalPlay } from '../../game/MoveValidator.js';
import { MoveEnumerator, type PlayMove } from '../MoveEnumerator.js';
import type { MoveScorer } from '../types.js';

/**
 * Rollout policy for the leaf-to-terminal portion of every ISMCTS iteration.
 * The plan calls for "Medium-tier evaluator with softmax move selection
 * (τ=0.5)" — random rollouts are weak in dominoes because most candidates
 * are non-scoring, so the leaf-value signal would be dominated by noise.
 * The evaluator is the variant's (`scorerFor`): the fives `Evaluator`, or
 * `KozyolEvaluator` for penalty scoring.
 *
 * Settles `pendingMuggins` immediately (always claim) so the rollout doesn't
 * need to model the timing window. Caps depth to prevent pathological loops
 * if the engine ever fails to converge — in practice every hand terminates
 * naturally because every play removes a tile.
 */
export interface RolloutOptions {
  readonly evaluator: MoveScorer;
  readonly enumerator: MoveEnumerator;
  readonly tau?: number;
  readonly rng?: () => number;
  readonly maxDepth?: number;
}

const DEFAULT_TAU = 0.5;
const DEFAULT_MAX_DEPTH = 300;
const SIM_TIME_STEP = 1;

export function runRollout(
  initialState: GameStateData,
  opts: RolloutOptions,
): GameStateData {
  const tau = opts.tau ?? DEFAULT_TAU;
  const rng = opts.rng ?? Math.random;
  const maxDepth = opts.maxDepth ?? DEFAULT_MAX_DEPTH;
  let state = settleMuggins(initialState);
  let now = 0;
  for (let depth = 0; depth < maxDepth && !state.handOver; depth += 1) {
    now += SIM_TIME_STEP;
    const seat = state.currentSeat;
    if (!canCurrentPlayerAct(state)) {
      state = applyPass(state, seat);
      continue;
    }
    const hand = state.hands[seat] ?? [];
    if (!hasLegalPlay(state.chain, hand, state.forcedLeadTile)) {
      // Draw if allowed, else pass — engine handles boneyard-empty.
      if (state.boneyard.length > 0 && state.ruleSet.drawRule !== 'no-draw') {
        state = applyDraw(state, seat);
      } else {
        state = applyPass(state, seat);
      }
      continue;
    }
    const candidates = opts.enumerator.enumerate(state.chain, hand, state.forcedLeadTile);
    if (candidates.length === 0) {
      state = applyPass(state, seat);
      continue;
    }
    const chosen = pickSoftmax(state, seat, candidates, opts.evaluator, tau, rng);
    state = applyMove(state, seat, chosen.tile, chosen.armIndex, now).state;
    state = settleMuggins(state);
  }
  return state;
}

function pickSoftmax(
  state: GameStateData,
  seat: number,
  candidates: readonly PlayMove[],
  evaluator: MoveScorer,
  tau: number,
  rng: () => number,
): PlayMove {
  const scores = candidates.map((c) => evaluator.score(state, seat, c));
  let maxScore = -Infinity;
  for (const s of scores) if (s > maxScore) maxScore = s;
  const weights = scores.map((s) => Math.exp((s - maxScore) / Math.max(0.01, tau)));
  let total = 0;
  for (const w of weights) total += w;
  if (total <= 0) return candidates[0]!;
  let r = rng() * total;
  for (let i = 0; i < candidates.length; i += 1) {
    r -= weights[i]!;
    if (r <= 0) return candidates[i]!;
  }
  return candidates[candidates.length - 1]!;
}

/** Always claim — rollouts assume the scorer locks in their points. */
function settleMuggins(state: GameStateData): GameStateData {
  if (!state.pendingMuggins) return state;
  return claimMuggins(state, state.pendingMuggins.scorerSeat, Date.now());
}

/**
 * Reward at terminal from `rootSeat`'s perspective. Uses score delta over
 * the search window (not absolute scores) — assumes the caller passed the
 * accumulated scores at search start. For partnership, sums teammates.
 *
 * Returns a value in [-1, +1]. Saturates at ±30 raw points so a single
 * high-scoring hand caps the reward instead of dominating the bandit.
 *
 * `rewardSign` is the scoring policy's: -1 for penalty scoring (Козёл),
 * where a score increase is a recorded penalty and therefore bad.
 */
export const REWARD_SATURATE = 30;

export function terminalReward(
  startScores: readonly number[],
  endScores: readonly number[],
  teamOf: readonly number[],
  rootSeat: number,
  rewardSign: 1 | -1 = 1,
): number {
  const myTeam = teamOf[rootSeat] ?? rootSeat;
  let myGain = 0;
  let oppGain = 0;
  for (let s = 0; s < endScores.length; s += 1) {
    const delta = (endScores[s] ?? 0) - (startScores[s] ?? 0);
    if ((teamOf[s] ?? s) === myTeam) myGain += delta;
    else oppGain += delta;
  }
  const diff = (myGain - oppGain) * rewardSign;
  return Math.max(-1, Math.min(1, diff / REWARD_SATURATE));
}
